/**
 * Cookie 会话管理。BaskReport 为 Session 鉴权（getLoginUser + SecurityUtils.decide），
 * 无 token header、无签名。需先登录拿到 Session Cookie 并在后续请求携带。
 *
 * 使用 undici + 手动 Cookie Jar 维护跨请求 Cookie。凭证由调用方提供，禁止硬编码。
 */

import { request } from 'undici';
import {
  isNonInteractive,
  DEFAULT_ACCESS_TOKEN_HEADER,
  DEFAULT_ACCESS_TOKEN_PREFIX,
  isOAuth2,
} from '../core/config.js';

export interface Session {
  baseUrl: string;
  /** 已捕获的 Set-Cookie 原始字符串，用于后续请求回带（仅 Session 模式使用）。 */
  cookies: string[];
  tenantId?: string;
  username: string;
  /** 鉴权模式：'session' 走 Cookie，'oauth2' 走 token header。 */
  authMode: 'session' | 'oauth2';
  /** OAuth2 访问令牌（仅 authMode='oauth2' 时设置）。 */
  accessToken?: string;
  /** OAuth2 token 注入的 header 名（仅 authMode='oauth2' 时设置）。 */
  accessTokenHeader?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public body?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 运行前健康检查失败：baskserver 不可达或返回异常状态码。 */
export class HealthCheckError extends Error {
  url: string;
  reason?: unknown;
  constructor(message: string, url: string, cause?: unknown) {
    super(message);
    this.name = 'HealthCheckError';
    this.url = url;
    this.reason = cause;
  }
}

/**
 * 运行前健康检查：确认 BaskReport 服务端（baskserver）可达且状态正常。
 * 端点：{baseUrl}/baskserver/api/health/check（公开接口，无需登录）。
 * 响应形如 {"status":"UP","timestamp":<毫秒时间戳>}；仅当 HTTP 2xx 且 status === 'UP' 视为健康。
 * 无法连接（DNS/连接拒绝/超时/非 2xx/非 UP/非法响应体）即抛出 HealthCheckError，由命令层兜底停止并提示用户。
 *
 * @param baseUrl 当前生效的服务端地址（来自 --base-url / BASK_BASE_URL / .env）。
 * @param timeoutMs 单次请求超时（毫秒），缺省 5000，避免连不上时长时间挂起。
 */
export async function checkHealth(baseUrl: string, timeoutMs = 5000): Promise<void> {
  const url = `${baseUrl.replace(/\/+$/, '')}/baskserver/api/health/check`;
  let resp;
  try {
    resp = await request(url, {
      method: 'GET',
      headersTimeout: timeoutMs,
      bodyTimeout: timeoutMs,
    });
  } catch (e) {
    throw new HealthCheckError(
      `无法连接到 BaskReport 服务端（健康检查失败）：${url}\n` +
        `请确认 baskserver 已启动、base-url 配置正确（--base-url / BASK_BASE_URL / .env）。\n` +
        `底层错误：${e instanceof Error ? e.message : String(e)}`,
      url,
      e,
    );
  }
  if (resp.statusCode < 200 || resp.statusCode >= 300) {
    throw new HealthCheckError(
      `BaskReport 服务端健康检查返回异常状态码 ${resp.statusCode}：${url}\n` +
        `请确认 baskserver 正常运行、base-url 指向正确的服务地址。`,
      url,
    );
  }
  // 解析响应体：status === 'UP' 才算健康（响应形如 {"status":"UP","timestamp":<毫秒时间戳>}）。
  const text = await resp.body.text();
  let status = '';
  try {
    const json = JSON.parse(text);
    status = (json && json.status) ?? '';
  } catch {
    throw new HealthCheckError(
      `BaskReport 服务端健康检查返回非法响应体（期望 {"status":"UP"}）：${url}\n` +
        `响应内容：${text.slice(0, 200)}`,
      url,
    );
  }
  if (status !== 'UP') {
    throw new HealthCheckError(
      `BaskReport 服务端健康检查未通过：status="${status}"（期望 "UP"）：${url}\n` +
        `请确认 baskserver 运行正常（应用可能正在启动或依赖未就绪）。`,
      url,
    );
  }
}

export interface LoginOptions {
  baseUrl: string;
  username: string;
  password: string;
  tenantId?: string;
  /** OAuth2 访问令牌；提供时跳过密码登录，直接进入 token header 模式。 */
  accessToken?: string;
  /** OAuth2 token 注入的 header 名，缺省 'authorization'（值形如 `Bearer <token>`）。 */
  accessTokenHeader?: string;
  /** 登录接口路径，缺省 /baskserver/api/login（依据后端约定）。 */
  loginPath?: string;
}

/**
 * 从响应头收集 Set-Cookie 并存入会话。undici v6 不再导出 CookieJar，
 * 这里用 setCookie/getCookies 维护一个轻量 cookie 列表。
 */
function collectCookies(resp: any, session: Session) {
  const sc = resp.headers ? resp.headers['set-cookie'] : undefined;
  if (sc) {
    // set-cookie 可能是字符串数组或字符串
    const list = Array.isArray(sc) ? sc : String(sc).split(/,(?=[^ ]*?=)/);
    for (const c of list) session.cookies.push(c.trim());
  }
}

function cookieHeader(session: Session): string {
  return session.cookies.map((c) => c.split(';')[0]).join('; ');
}

/**
 * 登录并建立会话。
 * - OAuth2 模式（提供 accessToken）：跳过 /login，直接构造携带 token 的会话，
 *   后续请求经 authedRequest 注入 token header（无需 Cookie）。
 * - Session 模式：登录接口一般接受表单或 JSON，返回 Set-Cookie；这里用 JSON 提交，
 *   失败再回退表单，以兼容不同部署。
 */
export async function login(opts: LoginOptions): Promise<Session> {
  // 运行前健康检查：所有联网命令都会先经过 login()，在此统一拦截「服务端不可达」。
  // 连不上即抛 HealthCheckError，由命令层 try/catch 兜底打印并停止（离线 --out 且不校验 SQL 时不走 login，不受影响）。
  await checkHealth(opts.baseUrl);

  // OAuth2 模式：无需调用登录接口，直接构造令牌会话。
  if (isOAuth2(opts)) {
    return {
      baseUrl: opts.baseUrl,
      cookies: [],
      tenantId: opts.tenantId,
      username: '', // OAuth2 无用户名概念
      authMode: 'oauth2',
      accessToken: opts.accessToken,
      accessTokenHeader: opts.accessTokenHeader ?? DEFAULT_ACCESS_TOKEN_HEADER,
    };
  }

  const session: Session = {
    baseUrl: opts.baseUrl,
    cookies: [],
    tenantId: opts.tenantId,
    username: opts.username,
    authMode: 'session',
  };
  const url = opts.loginPath ?? `${opts.baseUrl}/baskserver/api/login`;

  // 尝试 JSON 登录
  const resp = await request(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      username: opts.username,
      password: opts.password,
      tenantId: opts.tenantId,
    }),
  });
  const status = resp.statusCode;
  const text = await resp.body.text();
  collectCookies(resp, session);
  if (status !== 200) {
    throw new ApiError(`登录失败 (${status})：${text.slice(0, 200)}`, status, text);
  }
  return session;
}

/** 计算 OAuth2 token header 的值：默认 `Bearer <token>`，前缀可配置（如后端要求裸 token 设为空）。 */
function tokenHeaderValue(session: Session): string {
  const prefix =
    session.accessTokenHeader && session.accessTokenHeader.toLowerCase() === DEFAULT_ACCESS_TOKEN_HEADER
      ? DEFAULT_ACCESS_TOKEN_PREFIX
      : DEFAULT_ACCESS_TOKEN_PREFIX;
  return `${prefix}${session.accessToken}`;
}

/** 携带会话凭证发送请求：OAuth2 注入 token header，Session 注入 Cookie；二者均注入租户头。 */
export async function authedRequest(
  session: Session,
  url: string,
  init: any = {},
): Promise<any> {
  const headers = new Headers(init.headers ?? {});
  if (session.tenantId) headers.set('tenantId', session.tenantId);
  if (session.authMode === 'oauth2' && session.accessToken) {
    headers.set(session.accessTokenHeader ?? DEFAULT_ACCESS_TOKEN_HEADER, tokenHeaderValue(session));
  } else {
    const cookie = cookieHeader(session);
    if (cookie) headers.set('cookie', cookie);
  }
  const resp = await request(url, { ...init, headers });
  // OAuth2 模式无 Cookie 可收集；Session 模式继续维护跨请求 Cookie。
  if (session.authMode !== 'oauth2') collectCookies(resp, session);
  return resp;
}

/** 解析后端常见的 {success, message, data} 响应。 */
export async function parseJson(resp: Awaited<ReturnType<typeof authedRequest>>): Promise<any> {
  const text = await resp.body.text();
  try {
    const json = JSON.parse(text);
    if (json && typeof json === 'object' && 'success' in json && json.success === false) {
      throw new ApiError(`服务端返回错误：${json.message ?? ''}`, resp.statusCode, text);
    }
    return json;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    throw new ApiError(`无法解析响应：${text.slice(0, 200)}`, resp.statusCode, text);
  }
}

/**
 * 拉取当前登录用户的租户列表。
 * 端点：{baseUrl}/baskserver/api/user/get
 * 返回结构做了兼容：data.tenants / data.tenantList / 顶层 tenants，
 * 每个元素支持 { id } / { tenantId } / { code } / 字符串本身。
 */
export async function fetchUserTenants(session: Session): Promise<string[]> {
  const url = `${session.baseUrl}/baskserver/api/user/get`;
  const resp = await authedRequest(session, url, { method: 'GET' });
  if (resp.statusCode !== 200) {
    const text = await resp.body.text();
    throw new ApiError(`获取用户租户失败 (${resp.statusCode})：${text.slice(0, 200)}`, resp.statusCode, text);
  }
  const json = await parseJson(resp);
  const data = json?.data ?? json;
  const raw: any[] =
    data?.tenants ?? data?.tenantList ?? data?.tenantIds ?? json?.tenants ?? [];
  const ids: string[] = [];
  for (const t of raw) {
    if (t == null) continue;
    if (typeof t === 'string') ids.push(t);
    else if (typeof t === 'number') ids.push(String(t));
    else if (t.id != null) ids.push(String(t.id));
    else if (t.tenantId != null) ids.push(String(t.tenantId));
    else if (t.code != null) ids.push(String(t.code));
  }
  return ids;
}

/**
 * 解析最终使用的 tenantId：
 * - 已显式提供则直接使用（优先）。
 * - 未提供时拉取用户租户列表：仅一个则默认采用它；
 *   多个或无租户则抛错，要求通过 --tenantId 显式指定。
 *
 * 多租户时抛出 TenantSelectError，其 candidates 字段携带候选租户列表，
 * 供调用方（CLI / LLM）呈现给用户选择。
 */
export class TenantSelectError extends Error {
  candidates: string[];
  constructor(message: string, candidates: string[]) {
    super(message);
    this.name = 'TenantSelectError';
    this.candidates = candidates;
  }
}

export async function resolveTenant(session: Session, preferred?: string): Promise<string> {
  if (preferred && preferred.trim()) return preferred.trim();
  const tenants = await fetchUserTenants(session);
  if (tenants.length === 0) {
    throw new Error(
      '无法自动确定 tenantId：当前用户无任何租户，请通过 --tenantId / BASK_TENANT_ID / .env 显式指定。',
    );
  }
  if (tenants.length === 1) {
    return tenants[0]!;
  }
  // 多租户：把候选列表交给调用方（LLM / 交互式 CLI）让用户选择其中一个。
  throw new TenantSelectError(
    `当前用户有多个租户（${tenants.length} 个），无法自动确定 tenantId，请让用户从中选择一个并显式传入：\n` +
      tenants.map((t, i) => `  ${i + 1}. ${t}`).join('\n') +
      `\n选择后通过 --tenantId <选中的租户> / BASK_TENANT_ID / .env 重新执行命令。`,
    tenants,
  );
}

/**
 * 解析租户并在多租户时引导用户选择：
 * - 若 preferred 已给或只有一个租户，直接返回 tenantId。
 * - 多租户时：处于交互 TTY（人直接跑 CLI）用 readline 让用户选；
 *   否则（LLM / 管道驱动）把候选租户列表打印到 stderr 后抛出 TenantSelectError，
 *   交由上层（LLM）呈现给用户选择，再用 --tenantId 重跑。
 */
export async function resolveTenantOrSelect(session: Session, preferred?: string): Promise<string> {
  try {
    return await resolveTenant(session, preferred);
  } catch (e) {
    if (!(e instanceof TenantSelectError)) throw e;
    // 非交互模式（AI client 驱动 / 无 TTY / CI）：不走 readline，避免挂起。
    if (isNonInteractive() || !process.stdin.isTTY) {
      // 非交互：把候选交给上层（LLM）引导用户选择。
      process.stderr.write(`\n[e] ${e.message}\n`);
      throw e;
    }
    const { createInterface } = await import('node:readline/promises');
    const rl = createInterface({ input: process.stdin, output: process.stderr });
    try {
      process.stderr.write(`\n${e.message}\n请输入要使用的租户序号或租户 ID：`);
      const answer = (await rl.question('')).trim();
      const idx = Number(answer) - 1;
      const chosen = !Number.isNaN(idx) && e.candidates[idx] ? e.candidates[idx] : answer;
      if (!e.candidates.includes(chosen)) {
        throw new Error(`无效的租户选择：${answer}（候选：${e.candidates.join(', ')}）`);
      }
      return chosen;
    } finally {
      rl.close();
    }
  }
}
